import React, { useState, useEffect, useCallback } from 'react';
import { Search, ArrowUpRight, GitBranch, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function HistoryPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Admin-only project deletion: shown when /api/labs/whoami says is_admin.
  const [isAdmin, setIsAdmin] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadProjects = useCallback(() => {
    fetch(`${API_URL}/api/projects`, { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => { if (Array.isArray(data)) setProjects(data); setLoading(false); })
      .catch(err => {
        console.error('Failed to load projects:', err);
        // Retry once after 2 seconds on failure
        setTimeout(() => {
          fetch(`${API_URL}/api/projects`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : Promise.reject())
            .then(data => { if (Array.isArray(data)) setProjects(data); })
            .catch(() => {})
            .finally(() => setLoading(false));
        }, 2000);
      });
  }, []);

  useEffect(() => {
    loadProjects();
    fetch(`${API_URL}/api/labs/whoami`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(info => { if (info?.is_admin) setIsAdmin(true); })
      .catch(() => {});
  }, [loadProjects]);

  const handleCancelBuild = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm('Cancel this build and mark as failed?')) return;
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/cancel`, { method: 'POST', credentials: 'include' });
      if (res.ok) loadProjects();
    } catch {}
  };

  const handleMarkComplete = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm('Mark this build as completed?')) return;
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/mark-complete`, { method: 'POST', credentials: 'include' });
      if (res.ok) loadProjects();
    } catch {}
  };

  const handleDelete = async (e, project) => {
    e.stopPropagation();
    if (!window.confirm(`Permanently delete "${project.name}"?\nThis also removes its runs and chat history and cannot be undone.`)) return;
    setDeletingId(project.id);
    try {
      const res = await fetch(`${API_URL}/api/projects/${project.id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setProjects(prev => prev.filter(p => p.id !== project.id));
      } else {
        const j = await res.json().catch(() => ({}));
        alert(j.detail || `Delete failed (${res.status})`);
      }
    } catch (err) {
      alert('Delete failed: ' + (err.message || err));
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = projects.filter(p => {
    return !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.idea?.toLowerCase().includes(search.toLowerCase());
  });

  const stats = (() => {
    const buildingSet = new Set(['building', 'running']);
    const inFlightSet = new Set(['self_fixing', 'waiting_for_user']);
    const failedSet = new Set(['failed']);
    let building = 0, failed = 0, inFlight = 0, ready = 0;
    for (const p of projects) {
      const s = p.status;
      if (buildingSet.has(s)) building += 1;
      else if (inFlightSet.has(s)) inFlight += 1;
      else if (failedSet.has(s)) failed += 1;
      else ready += 1; // mirrors getStatusLabel fallback: anything else is "Ready"
    }
    return { total: projects.length, completed: ready, building, inFlight, failed };
  })();

  const getStatusLabel = (status) => {
    if (status === 'building' || status === 'running') return { text: 'Building', cls: 'badge-info' };
    if (status === 'self_fixing') return { text: 'Self-Fixing', cls: 'badge-warning' };
    if (status === 'waiting_for_user') return { text: 'Waiting', cls: 'badge-warning' };
    if (status === 'failed') return { text: 'Failed', cls: 'badge-error' };
    return { text: 'Ready', cls: 'badge-success' };
  };

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
  };

  return (
    <div data-testid="history-page">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '0.375rem' }}>Your builds</h1>
        <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem' }}>All your projects in one place</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div className="card stat-card"><div className="stat-value">{loading ? '—' : stats.total}</div><div className="stat-label">Total</div></div>
        <div className="card stat-card"><div className="stat-value" style={{ color: 'hsl(var(--success))' }}>{loading ? '—' : stats.completed}</div><div className="stat-label">Ready</div></div>
        <div className="card stat-card"><div className="stat-value" style={{ color: 'hsl(var(--info))' }}>{loading ? '—' : stats.building}</div><div className="stat-label">Building</div></div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
          <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'hsl(var(--muted-foreground))' }} />
          <input className="input" placeholder="Search projects..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ paddingLeft: '2.25rem' }} data-testid="project-history-search-input" />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ fontWeight: 600 }}>No projects found</p>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8125rem' }}>{search ? 'Try a different search' : 'Start a build from the Build page'}</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Project</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Repo</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</th>
                <th style={{ width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                return (
                  <tr key={p.id} style={{ borderBottom: '1px solid hsl(var(--border))', transition: 'background-color 0.15s', cursor: 'pointer' }}
                    onClick={() => window.location.href = `/project/${p.id}`}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(210 22% 98%)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    data-testid={`project-row-${p.id}`}
                  >
                    <td style={{ padding: '0.875rem 1rem' }}>
                      <div data-testid="project-row-open-button">
                        <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.name}{p.forked_from && <span style={{ marginLeft: '0.5rem', fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>(fork)</span>}</div>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.125rem', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.idea?.slice(0, 80)}</div>
                      </div>
                    </td>
                    <td style={{ padding: '0.875rem 1rem' }}>
                      {p.repo_url ? <a href={p.repo_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'hsl(var(--accent))', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }} data-testid="git-repo-url"><GitBranch size={13} /> Repo</a> : <span style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>&mdash;</span>}
                    </td>
                    <td style={{ padding: '0.875rem 1rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{formatDate(p.created_at)}</td>
                    <td style={{ padding: '0.875rem 1rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, p)}
                          disabled={deletingId === p.id}
                          title="Delete project (admin)"
                          data-testid={`project-delete-btn-${p.id}`}
                          style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            padding: '0.3rem', marginRight: '0.4rem',
                            color: deletingId === p.id ? 'hsl(var(--muted-foreground))' : '#ef4444',
                            opacity: deletingId === p.id ? 0.5 : 1,
                            verticalAlign: 'middle',
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                      <ArrowUpRight size={16} style={{ color: 'hsl(var(--muted-foreground))', verticalAlign: 'middle' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
